import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import ProductForm from "@/components/library/ProductForm";
import ProductDrawer from "@/components/library/ProductDrawer";
import { Search, Plus, Upload, LayoutGrid, List, X, ImageIcon, Bookmark } from "lucide-react";

const FILE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files`;
const money = (v) => (v == null || v === "" ? "—" : `$${Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`);

// Bookmarklet source (minified, single line, no external script, no quote-escaping in selectors).
const BOOKMARKLET = `javascript:(function(){function meta(n){var m=document.getElementsByTagName('meta');for(var i=0;i<m.length;i++){var k=(m[i].getAttribute('property')||m[i].getAttribute('name')||'').toLowerCase();if(k===n)return m[i].getAttribute('content')||''}return ''}function ld(){var s=document.getElementsByTagName('script');for(var i=0;i<s.length;i++){if((s[i].getAttribute('type')||'').toLowerCase().indexOf('ld+json')<0)continue;try{var j=JSON.parse(s[i].textContent);var a=j['@graph']||(j instanceof Array?j:[j]);for(var k=0;k<a.length;k++){var p=a[k];if(p&&(''+p['@type']).toLowerCase().indexOf('product')>=0)return p}}catch(e){}}return null}var p=ld()||{},o={};var h1=document.getElementsByTagName('h1')[0];o.name=p.name||meta('og:title')||(h1&&h1.innerText)||document.title||'';var b=p.brand;o.brand=b&&b.name?b.name:(typeof b=='string'?b:'');o.code=p.sku||p.mpn||'';var im=p.image;o.image=(im instanceof Array?im[0]:(im&&im.url?im.url:im))||meta('og:image')||'';var pr='';if(p.offers){var of=p.offers instanceof Array?p.offers[0]:p.offers;pr=of&&of.price?of.price:''}if(!pr){var ip=document.querySelectorAll('[itemprop=price]')[0];if(ip)pr=ip.getAttribute('content')||ip.innerText||''}if(!pr){var mm=(document.body.innerText||'').match(/\\$\\s?[\\d,]+(\\.\\d{2})?/);pr=mm?mm[0]:''}o.price=(''+pr).replace(/[^0-9.]/g,'');if(!o.code){var mc=(document.body.innerText||'').match(/(SKU|Code|Model|Item)\\s*[:#]?\\s*([A-Za-z0-9\\-\\/]+)/i);if(mc)o.code=mc[2]}if(!o.image){var ig=document.getElementsByTagName('img'),bw=0;for(var x=0;x<ig.length;x++){var w=ig[x].naturalWidth||ig[x].width||0,hh=ig[x].naturalHeight||ig[x].height||0;if(ig[x].src&&ig[x].src.indexOf('data:')<0&&w*hh>bw&&w>=200){bw=w*hh;o.image=ig[x].src}}}o.description=meta('og:description')||meta('description')||'';o.notes=(window.getSelection?window.getSelection().toString():'')||'';o.url=location.href;var q=Object.keys(o).map(function(k){return k+'='+encodeURIComponent(o[k]||'')}).join('&');q=q.substring(0,1800);window.open('APP_ORIGIN/library/clip?'+q,'_blank')})();`;

export default function Library() {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState({ products: [], suppliers: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [category, setCategory] = useState("all");
  const [supplier, setSupplier] = useState("all");
  const [status, setStatus] = useState("active");
  const [viewMode, setViewMode] = useState("grid");
  const [formOpen, setFormOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [clipInitial, setClipInitial] = useState(null);
  const [drawerId, setDrawerId] = useState(null);
  const [clipperOpen, setClipperOpen] = useState(false);
  const debRef = useRef(null);

  const bookmarkletHref = useMemo(() => BOOKMARKLET.replace("APP_ORIGIN", window.location.origin), []);

  // Clip flow: read query params and open the Add dialog prefilled.
  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    if (sp.get("url") || sp.get("name")) {
      setClipInitial({
        url: sp.get("url") || "", name: sp.get("name") || "", brand: sp.get("brand") || "",
        code: sp.get("code") || "", price: sp.get("price") || "", image: sp.get("image") || "",
        description: sp.get("description") || "", notes: sp.get("notes") || "",
      });
      setEditProduct(null); setFormOpen(true);
      navigate("/library", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => { clearTimeout(debRef.current); debRef.current = setTimeout(() => setDebouncedQ(q), 300); return () => clearTimeout(debRef.current); }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { status };
    if (debouncedQ.trim()) params.q = debouncedQ.trim();
    if (category !== "all") params.category = category;
    if (supplier !== "all") params.supplier = supplier;
    const r = await api.get("/library/products", { params });
    setData(r.data); setLoading(false);
  }, [debouncedQ, category, supplier, status]);
  useEffect(() => { load(); }, [load]);

  const drawerProduct = useMemo(() => data.products.find((p) => p.product_id === drawerId) || null, [data.products, drawerId]);

  const openEdit = (p) => { setClipInitial(null); setEditProduct(p); setFormOpen(true); setDrawerId(null); };
  const openAdd = () => { setClipInitial(null); setEditProduct(null); setFormOpen(true); };

  const activeChips = [
    category !== "all" && { key: "category", label: category, clear: () => setCategory("all") },
    supplier !== "all" && { key: "supplier", label: supplier, clear: () => setSupplier("all") },
    status !== "active" && { key: "status", label: "Archived", clear: () => setStatus("active") },
  ].filter(Boolean);

  const Thumb = ({ p, h }) => (p.images?.[0]
    ? <img src={`${FILE_BASE}/${p.images[0].path}`} alt={p.name} className={`w-full ${h} object-cover`} />
    : <div className={`w-full ${h} bg-zinc-100 flex flex-col items-center justify-center text-zinc-400 gap-1`}><ImageIcon className="h-6 w-6" /><span className="text-[10px] uppercase tracking-wider">{p.category}</span></div>);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1300px]">
      <header className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-4xl font-black tracking-tighter">Library</h1>
          <p className="text-zinc-500 mt-1 text-sm">Firm-wide materials &amp; products for specifying and pricing.</p>
          <p className="text-xs uppercase tracking-wider text-zinc-400 mt-1">{data.products.length} products · {data.suppliers.length} suppliers</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setClipperOpen(true)} className="rounded-sm gap-2" data-testid="library-get-clipper"><Bookmark className="h-4 w-4" /> Get the clipper</Button>
          <Button variant="outline" disabled title="coming soon" className="rounded-sm gap-2" data-testid="library-import-csv"><Upload className="h-4 w-4" /> Import CSV</Button>
          <Button onClick={openAdd} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-2" data-testid="library-add-product"><Plus className="h-4 w-4" /> Add product</Button>
        </div>
      </header>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, code, supplier, colour, tag…" className="pl-9 rounded-sm" data-testid="library-search" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-auto min-w-[150px] rounded-sm" data-testid="library-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {data.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={supplier} onValueChange={setSupplier}>
          <SelectTrigger className="w-auto min-w-[140px] rounded-sm" data-testid="library-supplier-filter"><SelectValue placeholder="All suppliers" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All suppliers</SelectItem>
            {data.suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-auto min-w-[110px] rounded-sm" data-testid="library-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 bg-zinc-100 rounded-sm p-1 ml-auto" data-testid="library-view-switcher">
          {[["grid", LayoutGrid], ["list", List]].map(([k, Icon]) => (
            <button key={k} onClick={() => setViewMode(k)} data-testid={`library-view-${k}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm transition-colors ${viewMode === k ? "bg-white shadow-sm text-zinc-900" : "text-zinc-500 hover:text-zinc-900"}`}>
              <Icon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
      </div>

      {activeChips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {activeChips.map((c) => (
            <button key={c.key} onClick={c.clear} data-testid={`chip-${c.key}`}
              className="inline-flex items-center gap-1 text-xs bg-zinc-100 text-zinc-700 rounded-sm px-2 py-1 hover:bg-zinc-200">
              {c.label} <X className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="h-8 w-8 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
      ) : data.products.length === 0 ? (
        <div className="border border-zinc-200 bg-white p-12 text-center rounded-sm">
          <ImageIcon className="h-8 w-8 text-zinc-300 mx-auto mb-3" />
          <h3 className="font-heading text-xl font-bold mb-1">No products yet</h3>
          <p className="text-zinc-500 text-sm">Add your first material or product to the library.</p>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {data.products.map((p) => (
            <button key={p.product_id} onClick={() => setDrawerId(p.product_id)} data-testid={`product-card-${p.product_id}`}
              className="text-left border border-zinc-200 bg-white rounded-sm overflow-hidden hover:border-zinc-300 transition-colors">
              <div className="h-40"><Thumb p={p} h="h-40" /></div>
              <div className="p-3">
                <p className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">{p.category}</p>
                <p className="font-medium text-sm mt-0.5 line-clamp-2">{p.name}</p>
                <p className="text-xs text-zinc-500 mt-0.5 truncate">{[p.supplier, p.code].filter(Boolean).join(" · ") || "—"}</p>
                <div className="flex items-end justify-between mt-2">
                  <span className="font-heading font-black">{money(p.cost_ex_gst)}<span className="text-xs text-zinc-400 font-normal"> /{p.unit}</span></span>
                  <span className="text-[10px] text-zinc-400">Used in {p.used_in || 0}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="border border-zinc-200 rounded-sm bg-white overflow-x-auto">
          <div className="min-w-[760px]">
            <div className="grid grid-cols-[2fr_1.2fr_1fr_0.8fr_0.8fr_0.6fr] px-4 py-3 border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-[0.15em] text-zinc-500">
              <span>Name</span><span>Supplier</span><span>Category</span><span>Code</span><span>Cost</span><span>Used</span>
            </div>
            {data.products.map((p) => (
              <button key={p.product_id} onClick={() => setDrawerId(p.product_id)} data-testid={`product-row-${p.product_id}`}
                className="w-full text-left grid grid-cols-[2fr_1.2fr_1fr_0.8fr_0.8fr_0.6fr] items-center px-4 py-2.5 border-b border-zinc-100 hover:bg-zinc-50 text-sm">
                <span className="flex items-center gap-2 min-w-0"><span className="h-8 w-8 rounded-sm overflow-hidden shrink-0 bg-zinc-100"><Thumb p={p} h="h-8" /></span><span className="truncate font-medium">{p.name}</span></span>
                <span className="truncate text-zinc-600">{p.supplier || "—"}</span>
                <span className="truncate text-zinc-500">{p.category}</span>
                <span className="truncate text-zinc-500">{p.code || "—"}</span>
                <span className="font-medium">{money(p.cost_ex_gst)}</span>
                <span className="text-zinc-400">{p.used_in || 0}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <ProductForm open={formOpen} onOpenChange={setFormOpen} product={editProduct} initial={clipInitial}
        categories={data.categories} suppliers={data.suppliers} onSaved={load} />
      <ProductDrawer open={!!drawerId} onOpenChange={(o) => !o && setDrawerId(null)}
        product={drawerProduct} onEdit={openEdit} onChanged={load} />

      <Dialog open={clipperOpen} onOpenChange={setClipperOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="clipper-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Clip to TWP</DialogTitle>
            <DialogDescription>Drag this to your bookmarks bar. On any supplier product page, click it.</DialogDescription>
          </DialogHeader>
          <div className="flex justify-center py-4">
            {/* Callback ref + setAttribute bypasses React's javascript: URL sanitiser so the bookmarklet drags correctly */}
            <a ref={(el) => { if (el) el.setAttribute("href", bookmarkletHref); }} onClick={(e) => e.preventDefault()} data-testid="clipper-bookmarklet"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white rounded-sm px-4 py-2 text-sm font-semibold cursor-grab active:cursor-grabbing">
              <Bookmark className="h-4 w-4" /> Clip to TWP
            </a>
          </div>
          <p className="text-xs text-zinc-500">
            It grabs the product name, price, code and main image from the page and opens the Library with the Add form prefilled — nothing is saved until you press Save.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
